import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from '../../utils/toast';
import { FaSave, FaShieldAlt, FaBell, FaClock } from 'react-icons/fa';

const DocumentVerificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    documentVerification: {
      enabled: false,
      requireVerificationFor: {
        formSubmissions: false,
        masterData: false,
        productTemplates: false,
        taskAttachments: false
      },
      verificationRoles: ['admin'],
      autoApproveAfterDays: 7,
      notificationSettings: {
        notifyOnUpload: true,
        notifyOnVerification: true,
        notifyOnRejection: true
      }
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/invoices/admin/settings');
      const settings = response.data;
      
      setFormData({
        documentVerification: {
          enabled: settings.documentVerification?.enabled || false,
          requireVerificationFor: {
            formSubmissions: settings.documentVerification?.requireVerificationFor?.formSubmissions || false,
            masterData: settings.documentVerification?.requireVerificationFor?.masterData || false,
            productTemplates: settings.documentVerification?.requireVerificationFor?.productTemplates || false,
            taskAttachments: settings.documentVerification?.requireVerificationFor?.taskAttachments || false
          },
          verificationRoles: settings.documentVerification?.verificationRoles || ['admin'],
          autoApproveAfterDays: settings.documentVerification?.autoApproveAfterDays || 7,
          notificationSettings: {
            notifyOnUpload: settings.documentVerification?.notificationSettings?.notifyOnUpload !== false,
            notifyOnVerification: settings.documentVerification?.notificationSettings?.notifyOnVerification !== false,
            notifyOnRejection: settings.documentVerification?.notificationSettings?.notifyOnRejection !== false
          }
        }
      });
    } catch (error) {
      toast.error('Failed to fetch settings');
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleNestedChange = (section, subsection, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const handleRoleChange = (role, checked) => {
    setFormData(prev => ({
      ...prev,
      documentVerification: {
        ...prev.documentVerification,
        verificationRoles: checked
          ? [...prev.documentVerification.verificationRoles, role]
          : prev.documentVerification.verificationRoles.filter(r => r !== role)
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      await api.put('/invoices/admin/settings', formData);
      toast.success('Checklist settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <FaShieldAlt className="text-2xl text-blue-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Checklist Settings</h1>
        </div>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <FaSave className="mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Main Settings */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">General Settings</h2>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.documentVerification.enabled}
                onChange={(e) => handleInputChange('documentVerification', 'enabled', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="enabled" className="ml-2 text-sm font-medium text-gray-700">
                Enable Checklist System
              </label>
            </div>
            
            <p className="text-sm text-gray-500">
              When enabled, uploaded documents will require approval from authorized users before being officially accepted.
            </p>
          </div>
        </div>

        {/* Document Types */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Document Types Requiring Verification</h2>
          
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="formSubmissions"
                checked={formData.documentVerification.requireVerificationFor.formSubmissions}
                onChange={(e) => handleNestedChange('documentVerification', 'requireVerificationFor', 'formSubmissions', e.target.checked)}
                disabled={!formData.documentVerification.enabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="formSubmissions" className="ml-2 text-sm font-medium text-gray-700">
                Form Submissions
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="masterData"
                checked={formData.documentVerification.requireVerificationFor.masterData}
                onChange={(e) => handleNestedChange('documentVerification', 'requireVerificationFor', 'masterData', e.target.checked)}
                disabled={!formData.documentVerification.enabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="masterData" className="ml-2 text-sm font-medium text-gray-700">
                Master Data Documents
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="productTemplates"
                checked={formData.documentVerification.requireVerificationFor.productTemplates}
                onChange={(e) => handleNestedChange('documentVerification', 'requireVerificationFor', 'productTemplates', e.target.checked)}
                disabled={!formData.documentVerification.enabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="productTemplates" className="ml-2 text-sm font-medium text-gray-700">
                Product Templates
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="taskAttachments"
                checked={formData.documentVerification.requireVerificationFor.taskAttachments}
                onChange={(e) => handleNestedChange('documentVerification', 'requireVerificationFor', 'taskAttachments', e.target.checked)}
                disabled={!formData.documentVerification.enabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="taskAttachments" className="ml-2 text-sm font-medium text-gray-700">
                Task Attachments
              </label>
            </div>
          </div>
        </div>

        {/* Verification Roles */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Verification Roles</h2>
          <p className="text-sm text-gray-500 mb-4">
            Select which user roles can verify documents:
          </p>
          
          <div className="space-y-3">
            {['admin', 'supervisor', 'manager'].map((role) => (
              <div key={role} className="flex items-center">
                <input
                  type="checkbox"
                  id={role}
                  checked={formData.documentVerification.verificationRoles.includes(role)}
                  onChange={(e) => handleRoleChange(role, e.target.checked)}
                  disabled={!formData.documentVerification.enabled}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <label htmlFor={role} className="ml-2 text-sm font-medium text-gray-700 capitalize">
                  {role}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Auto-approval Settings */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <FaClock className="text-lg text-blue-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Auto-approval Settings</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auto-approve after (days)
              </label>
              <input
                type="number"
                min="0"
                value={formData.documentVerification.autoApproveAfterDays}
                onChange={(e) => handleInputChange('documentVerification', 'autoApproveAfterDays', parseInt(e.target.value) || 0)}
                disabled={!formData.documentVerification.enabled}
                className="w-32 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
              <p className="text-sm text-gray-500 mt-1">
                Set to 0 to disable auto-approval. Documents will be automatically approved after this many days if not manually reviewed.
              </p>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <FaBell className="text-lg text-blue-600 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Notification Settings</h2>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notifyOnUpload"
                checked={formData.documentVerification.notificationSettings.notifyOnUpload}
                onChange={(e) => handleNestedChange('documentVerification', 'notificationSettings', 'notifyOnUpload', e.target.checked)}
                disabled={!formData.documentVerification.enabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="notifyOnUpload" className="ml-2 text-sm font-medium text-gray-700">
                Notify when document is uploaded
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notifyOnVerification"
                checked={formData.documentVerification.notificationSettings.notifyOnVerification}
                onChange={(e) => handleNestedChange('documentVerification', 'notificationSettings', 'notifyOnVerification', e.target.checked)}
                disabled={!formData.documentVerification.enabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="notifyOnVerification" className="ml-2 text-sm font-medium text-gray-700">
                Notify when document is verified
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="notifyOnRejection"
                checked={formData.documentVerification.notificationSettings.notifyOnRejection}
                onChange={(e) => handleNestedChange('documentVerification', 'notificationSettings', 'notifyOnRejection', e.target.checked)}
                disabled={!formData.documentVerification.enabled}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="notifyOnRejection" className="ml-2 text-sm font-medium text-gray-700">
                Notify when document is rejected
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DocumentVerificationSettings; 